import { useState } from "react";
import {
  Paper,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import TableChartIcon from "@mui/icons-material/TableChart";

import { AirframeCard } from "./AirframeCard";
import { AirframeGridView } from "./AirframeGridView";
import { Airframe } from "../../../../types/airframe";
import { Component } from "../../../../types/component";
import { SubAssembly } from "../../../../types/subAssembly";
import type { DisplayPreferences } from "../../../../hooks/useTenantDisplayPreferences";

type ViewMode = "list" | "grid";

type Props = {
  airframes: Airframe[];
  components: Component[];
  subAssemblies: SubAssembly[];
  loading: boolean;
  displayPreferences: DisplayPreferences;
  attached: (airframe: string) => any[];
  getInstalledComponentsForAirframe: (airframe: Airframe) => any[];
  getComponentDisplayName: (componentId: string) => string;
  onAttach: (p: Airframe) => void;
  onEdit: (p: Airframe) => void;
  onDelete: (p: Airframe) => void;
  onMiniPack: (p: Airframe) => void;
  onReplace: (p: Airframe, c: any) => void;
  onDetach: (componentId: string, airframeId?: string, slotId?: string) => void;
};

const toggleSx = {
  "& .MuiToggleButton-root": {
    color: "#aaa",
    borderColor: "#444",
    "&.Mui-selected": {
      color: "hsl(19 101% 51% / 1)",
      bgcolor: "hsl(19 101% 51% / 0.15)",
      "&:hover": {
        bgcolor: "hsl(19 101% 51% / 0.25)",
      },
    },
    "&:hover": {
      bgcolor: "rgba(255,255,255,0.05)",
    },
  },
};

export function AirframeList({
  airframes,
  components,
  loading,
  displayPreferences,
  ...actions
}: Props) {
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  if (loading) return <Typography sx={{ color: "#fff" }}>Loading...</Typography>;
  if (!airframes.length) return <Typography sx={{ color: "#fff" }}>No airframes</Typography>;

  return (
    <Paper sx={{ flex: 1, p: 2, bgcolor: "#111", color: "#fff", border: "1px solid #333" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h5" sx={{ color: "hsl(19 101% 51% / 1)" }}>
          Manage airframes
        </Typography>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(_, value: ViewMode | null) => {
            if (value) setViewMode(value);
          }}
          aria-label="airframe view mode"
          sx={toggleSx}
        >
          <Tooltip title="List view">
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Grid view">
            <ToggleButton value="grid" aria-label="grid view">
              <TableChartIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>

      {viewMode === "list" ? (
        airframes.map((p) => <AirframeCard key={p.id} pkg={p} displayPreferences={displayPreferences} {...actions} />)
      ) : (
        <AirframeGridView airframes={airframes} components={components} displayPreferences={displayPreferences} {...actions} />
      )}
    </Paper>
  );
}
